import Length from '../../../../source/js/shared/models/Geometry/Length';
import { MaximumDecimalPoints } from "@/Models/Validators/MaximumDecimalPoints";
import { MinimumValue } from "@/Models/Validators/MinimumValue";
import { MaximumValue } from "@/Models/Validators/MaximumValue";
import { ShapeConfigurationInterface } from './ShapeConfigurationInterface';
import Rectangle from "../../../../source/js/shared/models/Geometry/Rectangle";
import { ShapeCategory } from '@/types/shapeCategories';
import AbstractShape, { Dimensions } from '../Shapes/AbstractShape';
import { ValidatorInterface } from '../Validators/ValidatorInterface';
import { OrValidator } from '../Validators/OrValidator';

export default class JewelConfiguration implements ShapeConfigurationInterface {
	public hasNewValidators = true;

	get_dimension_keys(): string[] {
		return [
			'width_top',
			'width_bottom',
			'height_top',
			'height_bottom',
		];
	}

	get_modifier_keys(): string[] {
		return [];
	}

	get_maximum_dimensions( maximum_rectangle: Rectangle ): Record<string, Length> {
		const longest_side = maximum_rectangle.get_longest_side();
		const height_top = longest_side.divide(3).in_mm();
		const height_bottom = height_top + ( height_top / 2 );

		const width_top = maximum_rectangle.get_shortest_side().in_mm() - 1;

		return {
			width_top: new Length(width_top),
			width_bottom: maximum_rectangle.get_shortest_side(),
			height_top: new Length(height_top),
			height_bottom: new Length(height_bottom),
		};
	}

	// Maximum dimensions based on other dimensions
	get_maximum_variable_dimensions( dimensions: Dimensions ): Record<string, Length> {
		const maxWidthTop = dimensions.width_bottom - 1;
		return {
			width_top: Length.from_cm(maxWidthTop),
		};
	}

	get_minimum_dimensions( minimum: number, dimensions: Dimensions ): Record<string, Length>|null {
		return {
			width_top: Length.from_cm(minimum),
			width_bottom: Length.from_cm(dimensions.width_top),
			height_top: Length.from_cm(minimum),
			height_bottom: Length.from_cm(minimum),
		};
	}

	get_validators(): Record<string, Array<unknown>> {
		return {
			width_top: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
			width_bottom: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
			height_top: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
			height_bottom: [ MaximumDecimalPoints, MinimumValue, MaximumValue ],
		};
	}

	get_new_validators( instance: AbstractShape, minimum: number, maximums: Record<string, Length>, dimensions: Dimensions ): ValidatorInterface[] {
		const maxWidthTop = dimensions.width_bottom - 1;
		const minWidthBottom = dimensions.width_top + 1;

		return [
			new OrValidator( [
				new MaximumValue( {
					field: 'jewel-width_top',
					dimension: 'width_top',
					instance,
					maximums: {
						width_top: Length.from_cm(maxWidthTop),
						width_bottom: maximums.width_bottom,
					},
				} ),
				new MinimumValue( {
					field: 'jewel-width_bottom',
					dimension: 'width_bottom',
					instance,
					minimums: {
						width_bottom: Length.from_cm(minWidthBottom),
					},
				} ),
			]),
			new MaximumValue( {
				field: 'jewel-width_top',
				dimension: 'width_top',
				instance,
				maximums,
			} ),
			new MinimumValue( {
				field: 'jewel-width_bottom',
				dimension: 'width_bottom',
				minimum,
				instance,
			} ),
			new MaximumValue( {
				field: 'jewel-height_top',
				dimension: 'height_top',
				instance,
				maximums,
			} ),
			new MinimumValue( {
				field: 'jewel-height_bottom',
				dimension: 'height_bottom',
				minimum,
				instance,
			} ),
			new MaximumDecimalPoints( {
				field: 'jewel-width_top',
				dimension: 'width_top',
				decimalPoints: 1,
				instance,
			} ),
			new MaximumDecimalPoints( {
				field: 'jewel-width_bottom',
				dimension: 'width_bottom',
				decimalPoints: 1,
				instance,
			} ),
			new MaximumDecimalPoints( {
				field: 'jewel-height_top',
				dimension: 'height_top',
				decimalPoints: 1,
				instance,
			} ),
			new MaximumDecimalPoints( {
				field: 'jewel-height_bottom',
				dimension: 'height_bottom',
				decimalPoints: 1,
				instance,
			} ),
		];
	}

	get_cutout_validators( instance: AbstractShape, dimensions: Dimensions ): ValidatorInterface[] {
		const maxWidthTop = dimensions.width_bottom - 1;
		const minWidthBottom = dimensions.width_top + 1;

		return [
			new OrValidator( [
				new MaximumValue( {
					field: 'jewel-width_top',
					dimension: 'width_top',
					instance,
					maximums: {
						width_top: Length.from_cm(maxWidthTop),
					},
				} ),
				new MinimumValue( {
					field: 'jewel-width_bottom',
					dimension: 'width_bottom',
					instance,
					minimums: {
						width_bottom: Length.from_cm(minWidthBottom),
					},
				} ),
			]),
		];
	}

	get_category(): ShapeCategory {
		return 'complex';
	}
}
